import { useState, useContext, useEffect } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { ellisDntChatApiRef } from '../../../apis/ellisDntChatApi';
import { AuthContext } from '../../../providers/AuthProvider';
import type { ChatMessage, AgentType, Attachment } from '../types/chat';

type Message = ChatMessage;

interface UseChatParams {
  setMessages: (updater: (prev: Message[]) => Message[]) => void;
  messages: Message[];
  currentAgent: AgentType;
  agentName: string;
}

interface UseChatReturn {
  sendMessage: (content: string, attachments?: Attachment[]) => Promise<void>;
  isLoading: boolean;
  thinkingText: string;
  stopMessage: () => void;
}

type ChunkType =
  | 'assistant_text'
  | 'tool_call'
  | 'tool_result'
  | 'status'
  | 'output'
  | 'done'
  | 'unknown';

interface ParsedChunk {
  type: ChunkType;
  text: string;
  thinkingText: string;
  isFinal: boolean;
}

interface StreamEnvelope {
  chunk?: {
    type?: string;
    content?: string;
    name?: string;
    summary?: string;
    final_turn?: boolean;
    success?: boolean;
  };
  is_final?: boolean;
}

// Tool type mapping
const TOOL_MAP: Partial<Record<AgentType, string>> = {
  'jira-agile': 'jira',
  'ado-agile': 'ado',
  'ado-code': 'ado',
  'knowledge-graph': 'knowledge-graph',
};

const parseStreamEvent = (rawData: string): ParsedChunk => {
  const normalized = rawData.replace(/^data:\s*/, '').trim();

  if (!normalized || normalized === '[DONE]') {
    return { type: 'done', text: '', thinkingText: '', isFinal: true };
  }

  try {
    const envelope = JSON.parse(normalized) as StreamEnvelope;
    const chunk = envelope.chunk;
    const chunkType = (chunk?.type ?? 'unknown') as ChunkType;
    const isFinal = Boolean(envelope.is_final);

    switch (chunkType) {
      // Streaming assistant prose — accumulate in the message bubble
      case 'assistant_text':
        return {
          type: chunkType,
          text: chunk?.content ?? '',
          thinkingText: '',
          isFinal,
        };

      // Definitive final output — replace accumulated text and finish
      case 'output':
        return {
          type: chunkType,
          text: chunk?.content ?? '',
          thinkingText: '',
          isFinal: true,
        };

      // Tool being invoked — show in thinking indicator
      case 'tool_call':
        return {
          type: chunkType,
          text: '',
          thinkingText: chunk?.name
            ? `Calling ${chunk.name}…`
            : 'Calling tool…',
          isFinal,
        };

      // Tool finished — clear thinking indicator
      case 'tool_result':
        return { type: chunkType, text: '', thinkingText: '', isFinal };

      // Generic processing status
      case 'status':
        return {
          type: chunkType,
          text: '',
          thinkingText: chunk?.content ?? 'Processing…',
          isFinal,
        };

      default:
        return { type: 'unknown', text: '', thinkingText: '', isFinal };
    }
  } catch {
    return {
      type: 'assistant_text',
      text: normalized,
      thinkingText: '',
      isFinal: false,
    };
  }
};

// Format attachment for API payload
const formatAttachmentForApi = (att: Attachment) => ({
  id: att.id,
  url: att.sharepointUrl || att.url,
  name: att.name,
  type: att.type,
});

export const useChat = ({
  setMessages,
  messages,
  currentAgent,
  agentName,
}: UseChatParams): UseChatReturn => {
  const identityApi = useApi(identityApiRef);
  const ellisDntChatApi = useApi(ellisDntChatApiRef);
  const { profEmail } = useContext(AuthContext);

  const [isLoading, setIsLoading] = useState(false);
  const [thinkingText, setThinkingText] = useState('');
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);

  // Clear loading/thinking state when agent changes
  useEffect(() => {
    setIsLoading(false);
    setThinkingText('');
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
  }, [currentAgent]);

  const sendMessage = async (
    content: string,
    attachments?: Attachment[],
  ): Promise<void> => {
    const newAbortController = new AbortController();
    setAbortController(newAbortController);

    if (!content.trim()) return;

    const assistantMsgId = (Date.now() + 1).toString();

    const assistantMsg: Message = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      agent: currentAgent,
      timestamp: Date.now(),
      isComplete: false,
    };

    setMessages(prev => [...prev, assistantMsg]);
    setIsLoading(true);
    setThinkingText('');

    const markComplete = (finalContent?: string) => {
      setMessages(prev => {
        const lastMsg = prev[prev.length - 1];
        if (lastMsg && lastMsg.id === assistantMsgId) {
          return [
            ...prev.slice(0, -1),
            {
              ...lastMsg,
              ...(finalContent !== undefined && { content: finalContent }),
              isComplete: true,
            },
          ];
        }
        return prev;
      });
      setIsLoading(false);
      setThinkingText('');
    };

    const markError = (error: unknown) => {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      setMessages(prev => {
        const lastMsg = prev[prev.length - 1];
        if (lastMsg && lastMsg.id === assistantMsgId) {
          return [
            ...prev.slice(0, -1),
            {
              ...lastMsg,
              content: `[${agentName}] Error: ${msg}`,
              isComplete: true,
            },
          ];
        }
        return prev;
      });
      setIsLoading(false);
      setThinkingText('');
    };

    try {
      const credentials = await identityApi.getCredentials();
      const accessToken = credentials.token || '';
      const tool = TOOL_MAP[currentAgent] ?? 'jira';

      // Build conversation history
      const conversationHistory = messages
        .filter(msg => msg.role === 'user' || msg.role === 'assistant')
        .map(msg => ({
          role: msg.role,
          content: msg.content,
          ...(msg.attachments && {
            attachments: msg.attachments.map(formatAttachmentForApi),
          }),
        }));

      // Add new user message
      conversationHistory.push({
        role: 'user',
        content,
        ...(attachments && {
          attachments: attachments.map(formatAttachmentForApi),
        }),
      });

      await ellisDntChatApi.chatStream(
        { message: conversationHistory, tool },
        accessToken,
        () => {}, // onStart
        () => {
          markComplete();
          setAbortController(null);
        },
        (data: string) => {
          const parsed = parseStreamEvent(data);

          switch (parsed.type) {
            case 'assistant_text':
              if (parsed.text) {
                setThinkingText(prev =>
                  prev ? `${prev}\n${parsed.text}` : parsed.text,
                );
              }
              break;
            case 'output':
              markComplete(parsed.text);
              break;
            case 'tool_call':
            case 'status':
              if (parsed.thinkingText) {
                setThinkingText(prev =>
                  prev
                    ? `${prev}\n${parsed.thinkingText}`
                    : parsed.thinkingText,
                );
              }
              break;
            case 'tool_result':
            case 'done':
              if (parsed.type === 'done') markComplete();
              break;
          }
        },
        (error: unknown) => {
          console.error('Ellis DNT Chat API error:', error);
          markError(error);
          setAbortController(null);
        },
        profEmail,
        newAbortController.signal,
      );
    } catch (error) {
      console.error('Error sending message:', error);
      markError(error);
    }
  };

  const stopMessage = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setIsLoading(false);
      setThinkingText('');
    }
  };

  return { sendMessage, isLoading, thinkingText, stopMessage };
};
